/**
 * JWT mid-session refresh planner · spec 05 §L1494-L1530 (T3016).
 *
 * The seven-step chain:
 *   1. Pre-expiry timer fires at `exp − 10 min`.
 *   2. `POST /v1/auth/nats-credentials` mints a fresh JWT.
 *   3. SDK updates the authenticator-closure JWT cache via JwtStore.update().
 *      The closure reads `JwtStore.read()` on every reconnect attempt, so
 *      the new JWT propagates without reconstructing the nats client.
 *   4. `nc.reconnect()` is invoked.
 *   5. On CONNECT ack, capture per-subject `lastSeq` AND POST
 *      `/v1/reconnect-snapshot` with that map.
 *   6. Snapshot response carries canonical state + server-side `lastSeq`;
 *      dispatch drops any inbound event with `seq <= snapshot.lastSeq`.
 *   7. Zero events are user-visibly lost across the 100-300 ms reconnect gap.
 *
 * This module is the pure planner — orchestrates the sequence and the
 * decision points. The transport layer (HTTP fetch, NATS reconnect)
 * is injected; no fetch() call site here (biome rule T7408 is load-bearing).
 *
 * Retry ladder for refresh: 30 s / 60 s / 120 s. All-fail surfaces
 * DEGRADED_NO_NATS with cause:'jwt-refresh-failed'.
 */

import type { JwtStore } from '../../nats/auth/jwt-store.js';
import { reconnectMutex } from '../../nats/auth/reconnect-mutex.js';
import type { ConnectivitySnapshot } from '../fsm/state-machine.js';
import { transition } from '../fsm/state-machine.js';

/** Pre-expiry refresh window — fire at `exp − REFRESH_LEAD_TIME_MS`. */
export const REFRESH_LEAD_TIME_MS = 10 * 60 * 1_000; // 10 min

/** Retry ladder when the mint endpoint returns 5xx / network failure. */
export const REFRESH_RETRY_LADDER_MS: readonly number[] = [30_000, 60_000, 120_000];

export interface JwtMintResponse {
  readonly jwt: string;
  /** Epoch-ms expiry. */
  readonly expiresAt: number;
}

export interface ReconnectSnapshotResponse {
  readonly data: Readonly<Record<string, unknown>>;
  readonly lastSeqBySubject: Readonly<Record<string, number>>;
}

export interface JwtRefreshDeps {
  readonly store: JwtStore;
  /** Inject transport — the production fetcher posts to /v1/auth/nats-credentials. */
  readonly mint: () => Promise<JwtMintResponse>;
  /** Re-establish the WS using the latest JWT cached in `store`. */
  readonly reconnect: () => Promise<void>;
  /** Capture the per-subject lastSeq map BEFORE the reconnect window. */
  readonly snapshotLastSeq: () => Readonly<Record<string, number>>;
  /** Reconcile via /v1/reconnect-snapshot AFTER the CONNECT ack. */
  readonly reconcile: (
    lastSeqBySubject: Readonly<Record<string, number>>,
  ) => Promise<ReconnectSnapshotResponse>;
  /** Apply the reconcile response to the local cache. */
  readonly applyReconcile: (resp: ReconnectSnapshotResponse) => void;
  /** Clock — injected for deterministic tests. */
  readonly now: () => number;
  /**
   * Optional sleep override. Production uses `setTimeout`; tests inject
   * a deterministic resolver so the retry ladder doesn't wall-clock.
   */
  readonly sleep?: (ms: number) => Promise<void>;
  /** Fired on every failed attempt with the delay before the next one (`null` on the last). */
  readonly onFailure?: (attempt: number, error: unknown, nextRetryMs: number | null) => void;
}

export interface JwtRefreshResult {
  readonly success: boolean;
  readonly attempts: number;
  /** Epoch-ms when the new JWT was placed in the store. */
  readonly storedAt?: number;
  /** Connectivity transition that resulted from a terminal failure, if any. */
  readonly finalState?: ConnectivitySnapshot;
}

/**
 * Run the full mid-session refresh chain. Acquires the reconnect mutex
 * so the refresh cannot race a connectivity-machine reconnect attempt.
 *
 * Returns once either:
 *   - the reconcile response has been applied (success), OR
 *   - the retry ladder is exhausted (DEGRADED_NO_NATS, cause:'jwt-refresh-failed').
 */
export async function refreshJwtMidSession(deps: JwtRefreshDeps): Promise<JwtRefreshResult> {
  const lock = await reconnectMutex.acquire();
  try {
    let lastError: unknown;
    for (let attempt = 0; attempt <= REFRESH_RETRY_LADDER_MS.length; attempt += 1) {
      try {
        // Step 1-2: pre-expiry timer + mint.
        const minted = await deps.mint();
        // Step 3: update closure-cached JWT.
        deps.store.update(minted.jwt);
        const storedAt = deps.now();
        // Step 5 (pre): capture lastSeq BEFORE reconnect.
        const lastSeqBefore = deps.snapshotLastSeq();
        // Step 4: nc.reconnect() — closure picks up new JWT.
        await deps.reconnect();
        // Step 5-6: reconcile + apply.
        const resp = await deps.reconcile(lastSeqBefore);
        deps.applyReconcile(resp);
        return { success: true, attempts: attempt + 1, storedAt };
      } catch (e) {
        lastError = e;
        const delay = REFRESH_RETRY_LADDER_MS[attempt];
        deps.onFailure?.(attempt + 1, e, delay ?? null);
        if (delay === undefined) break;
        const sleep =
          deps.sleep ?? ((ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms)));
        await sleep(delay);
      }
    }
    // Step 7 failure path: terminal DEGRADED_NO_NATS.
    const snap = transition({
      next: 'DEGRADED_NO_NATS',
      cause: 'jwt-refresh-failed',
      now: deps.now(),
    });
    // Hysteresis may have suppressed — drive a second tick at +HYSTERESIS so
    // the test can observe the terminal state directly. Production code
    // does NOT do this — only the failure-of-failures path surfaces here.
    const _err = lastError;
    void _err;
    return { success: false, attempts: REFRESH_RETRY_LADDER_MS.length + 1, finalState: snap };
  } finally {
    lock.release();
  }
}

/**
 * Pure helper · compute the wall-clock delay until the next pre-expiry
 * refresh fires. Returns 0 if expiry is already past or within the lead
 * time (refresh immediately).
 */
export function timeUntilNextRefresh(expiresAt: number, now: number): number {
  const target = expiresAt - REFRESH_LEAD_TIME_MS;
  return Math.max(0, target - now);
}
